"use client";

import { CalendarCheck, MessageCircle, Phone, UserRound } from "lucide-react";
import {
  FadeIn,
  HomeEyebrow,
  HomeHeading,
  HomeLead,
  HomeSection,
} from "~/components/siteglue/home/section-shell";

const STEPS = [
  {
    icon: Phone,
    title: "Answer",
    body: "The agent answers calls and talks with people on your website.",
  },
  {
    icon: MessageCircle,
    title: "Assist",
    body: "It handles questions from the information you publish for the business.",
  },
  {
    icon: CalendarCheck,
    title: "Schedule",
    body: "It schedules an appointment when Calendly, Google Calendar, NexHealth, or Juvonno is connected. Otherwise it captures the appointment request.",
  },
  {
    icon: UserRound,
    title: "Connect",
    body: "It routes the conversation to the right person when a teammate is needed or when the visitor asks.",
  },
] as const;

/** Answer → Assist → Schedule → Connect. Shared by the live and legacy homepages. */
export function SiteGlueHomeHirePath() {
  return (
    <HomeSection id="answer-assist-schedule-connect" tone="white">
      <FadeIn>
        <HomeEyebrow>How it works</HomeEyebrow>
        <HomeHeading>Answer, assist, schedule, and connect.</HomeHeading>
        <HomeLead>
          The agent can finish many requests. A person steps in when the conversation needs one,
          or when someone asks for the team.
        </HomeLead>
      </FadeIn>

      <ol className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((item, i) => (
          <FadeIn key={item.title} delay={0.08 * (i + 1)}>
            <li className="relative h-full">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-800">
                <item.icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.body}</p>
            </li>
          </FadeIn>
        ))}
      </ol>

      <FadeIn delay={0.2} className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#4f81bd]">Upcoming</p>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">
          Get ready for conversations initiated by personal AI agents.
        </p>
      </FadeIn>
    </HomeSection>
  );
}
